import React from 'react';
import { Badge, ErrorState, Table } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { EMPTY } from '../../lib/format';
import { num } from '../../lib/aiPricingView';
import { INVOCATION_QUERY } from './queries';
import {
  BlockSkeleton, Code, CountCell, DetailDrawer, Fact, ParsehCell, PriceSourceBadge, UsdCell, useEnumLabel,
} from './shared';

const CALL_STATUS_VARIANT = { ok: 'success', error: 'destructive', fallback: 'warning' };

/**
 * One invocation and its calls (design §12.7): step, role, model, tokens,
 * audio seconds, computed vs provider-reported cost, latency, status — plus
 * the wallet transaction id, which is the join to what was actually debited.
 * No PHI lives here by design (§14): ids and numbers only.
 */
export default function InvocationDrawer({ requestId, onClose, labelOf }) {
  const { t, formatDateTime, formatNumber } = useTranslation();
  const statusLabel = useEnumLabel('aiPricing.ledger.status');
  const ownerLabel = useEnumLabel('aiPricing.overview.owner');
  const reasonLabel = useEnumLabel('aiPricing.ledger.chargeReason');
  const roleLabel = useEnumLabel('aiPricing.services.role');

  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(INVOCATION_QUERY, { requestId })
      .then((d) => setDetail(d?.aiInvocation || null))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [requestId, t]);

  React.useEffect(() => { load(); }, [load]);

  const inv = detail?.invocation;
  const seconds = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 1 }));

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={inv ? labelOf(inv.serviceKey) : t('aiPricing.ledger.drawer.title')}
      sub={<Code>{requestId}</Code>}
      data-testid="ai-ledger-drawer"
    >
      {loading ? (
        <div data-state="loading" role="status" aria-busy="true" aria-label={t('aiPricing.ledger.drawer.loading')}>
          <BlockSkeleton lines={6} height={18} />
        </div>
      ) : error || !detail ? (
        <ErrorState
          title={t('aiPricing.ledger.drawer.error')}
          description={error || t('errors.region.noAnswer')}
          onRetry={load}
          retryLabel={t('common.actions.retry')}
          data-testid="ai-ledger-drawer-error"
        />
      ) : (
        <div className="flex flex-col gap-6" data-state="content">
          <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
            <Fact label={t('aiPricing.ledger.drawer.requestId')} value={inv.requestId} ltr />
            <Fact label={t('aiPricing.ledger.drawer.owner')} value={<span>{ownerLabel(inv.ownerType)} <Code>{inv.ownerId}</Code></span>} />
            <Fact label={t('aiPricing.ledger.drawer.appointment')} value={inv.appointmentId} ltr />
            <Fact label={t('aiPricing.ledger.drawer.started')} value={formatDateTime(inv.startedAt)} />
            <Fact label={t('aiPricing.ledger.drawer.finished')} value={inv.finishedAt ? formatDateTime(inv.finishedAt) : EMPTY} />
            <Fact label={t('aiPricing.ledger.columns.status')} value={<Badge variant={CALL_STATUS_VARIANT[inv.status] || 'warning'}>{statusLabel(inv.status)}</Badge>} />
            <Fact label={t('aiPricing.ledger.columns.cost')} value={<span><UsdCell value={inv.costUsd} /> {t('common.units.usd')}</span>} />
            <Fact label={t('aiPricing.ledger.columns.charged')} value={<span><ParsehCell value={inv.chargedParseh} /> {t('common.units.parseh')}</span>} />
            <Fact label={t('aiPricing.ledger.drawer.chargeReason')} value={reasonLabel(inv.chargeReason)} />
            <Fact label={t('aiPricing.ledger.drawer.priceSource')} value={inv.priceSource ? <PriceSourceBadge source={inv.priceSource} /> : EMPTY} />
            <Fact label={t('aiPricing.ledger.drawer.wallet')} value={inv.walletTransactionId} ltr />
            {inv.billedMinutes != null ? <Fact label={t('aiPricing.ledger.drawer.billedMinutes')} value={seconds(inv.billedMinutes)} /> : null}
            {inv.audioSeconds != null ? <Fact label={t('aiPricing.ledger.drawer.audioSeconds')} value={seconds(inv.audioSeconds)} /> : null}
          </dl>

          <section>
            <h3 className="mb-2 text-sm font-bold text-foreground">{t('aiPricing.ledger.drawer.calls')}</h3>
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table
                dense
                ariaLabel={t('aiPricing.ledger.drawer.callsAria')}
                rows={detail.calls || []}
                rowKey={(c) => c.id}
                columns={[
                  { key: 'step', header: t('aiPricing.ledger.drawer.columns.step'), render: (c) => <Code>{c.step}</Code> },
                  { key: 'role', header: t('aiPricing.ledger.drawer.columns.role'), render: (c) => roleLabel(c.role) },
                  { key: 'model', header: t('aiPricing.ledger.drawer.columns.model'), render: (c) => <Code title={c.modelId}>{c.modelId}</Code> },
                  {
                    key: 'tokens',
                    header: t('aiPricing.ledger.drawer.columns.tokens'),
                    align: 'end',
                    render: (c) => (
                      <span className="tabular-nums">
                        {formatNumber(c.inputTokens ?? 0)} / {formatNumber(c.outputTokens ?? 0)} / {formatNumber(c.cachedTokens ?? 0)}
                        {c.tokensEstimated ? <Badge variant="outline" className="ms-1">{t('aiPricing.ledger.drawer.estimated')}</Badge> : null}
                      </span>
                    ),
                  },
                  { key: 'audio', header: t('aiPricing.ledger.drawer.columns.audio'), align: 'end', render: (c) => seconds(c.audioSeconds) },
                  { key: 'cost', header: t('aiPricing.ledger.drawer.columns.cost'), align: 'end', render: (c) => <UsdCell value={c.costUsd} /> },
                  { key: 'providerCost', header: t('aiPricing.ledger.drawer.columns.providerCost'), align: 'end', render: (c) => <UsdCell value={c.providerCostUsd} /> },
                  { key: 'latency', header: t('aiPricing.ledger.drawer.columns.latency'), align: 'end', render: (c) => <CountCell value={c.latencyMs} /> },
                  { key: 'status', header: t('aiPricing.ledger.drawer.columns.status'), render: (c) => <Badge variant={CALL_STATUS_VARIANT[c.status] || 'outline'}>{statusLabel(c.status)}</Badge> },
                ]}
                empty={<p className="p-4 text-sm text-muted-foreground">{t('aiPricing.overview.breakdownEmpty')}</p>}
                data-testid="ai-ledger-drawer-calls"
              />
            </div>
          </section>
        </div>
      )}
    </DetailDrawer>
  );
}
